import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import ParticleCanvas from './ParticleCanvas';

const ROTATING_PHRASES = [
  "become a bookkeeper.",
  "decode the tax code.",
  "run payroll at midnight.",
  "google \u201cwhat is a 941.\u201d",
  "read California labor law.",
  "write an employee handbook.",
  "drown in loan paperwork.",
  "decipher insurance fine print.",
  "reconcile QuickBooks on Sundays.",
  "untangle sales tax.",
  "guess what your company\u2019s worth.",
  "do everyone else\u2019s job too.",
];

const KEYWORD_MAP = [
  { keywords: ['tax', 'bookkeep', 'cpa', 'account', 'irs', '941', 'quickbook', 'deduct', 'write-off'], category: 'cpa' },
  { keywords: ['payroll', 'pay stub', 'w-2', 'w2', 'gusto', 'adp', 'wages'], category: 'payroll' },
  { keywords: ['loan', 'bank', 'fund', 'sba', 'credit', 'borrow'], category: 'banker' },
  { keywords: ['hr', 'hir', 'handbook', 'employ', 'onboard', 'terminat'], category: 'hr' },
  { keywords: ['insur', 'liabil', 'coverage', 'policy', 'premium'], category: 'insurance' },
  { keywords: ['retir', 'exit', 'sell', 'succession', '401k', 'pension'], category: 'retirement' },
];

const HINT_CHIPS = [
  { label: "Taxes & bookkeeping", search: "taxes" },
  { label: "Payroll", search: "payroll" },
  { label: "Business loan", search: "loan" },
  { label: "HR & hiring", search: "hiring" },
  { label: "Insurance", search: "insurance" },
];

const prefersReducedMotion = typeof window !== 'undefined' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const matchCategory = (text) => {
  const lower = text.toLowerCase();
  for (const { keywords, category } of KEYWORD_MAP) {
    if (keywords.some(kw => lower.includes(kw))) return category;
  }
  return null;
};

const HeroSection = () => {
  const navigate = useNavigate();
  const [visible, setVisible] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [questions, setQuestions] = useState([]);
  const [questionsLoading, setQuestionsLoading] = useState(true);

  const [phraseIndex, setPhraseIndex] = useState(0);
  const [phraseVisible, setPhraseVisible] = useState(true);

  const [searchInput, setSearchInput] = useState('');
  const quizRef = useRef(null);

  const [quizShown, setQuizShown] = useState(false);
  const [quizStep, setQuizStep] = useState(0);
  const [quizMinStep, setQuizMinStep] = useState(0);
  const [quizAnswers, setQuizAnswers] = useState({});
  const [quizAnimating, setQuizAnimating] = useState(false);
  const [quizCardVisible, setQuizCardVisible] = useState(true);
  const [quizSearchText, setQuizSearchText] = useState('');

  // ── Phrase-slot height measurement ───────────────────────────────────────
  // A hidden off-screen <em> (measureElRef) lives inside the slot container
  // so it inherits the exact same width and font styles as the visible phrase.
  // We iterate every phrase through it on mount and every resize, then lock
  // the slot to that maximum height so nothing below the h1 ever shifts.
  const measureElRef = useRef(null);
  const [slotHeight, setSlotHeight] = useState(0);

  useEffect(() => {
    const measureAll = () => {
      const el = measureElRef.current;
      if (!el) return;
      let max = 0;
      for (const phrase of ROTATING_PHRASES) {
        el.textContent = phrase;
        max = Math.max(max, el.offsetHeight);
      }
      el.textContent = '';
      if (max > 0) setSlotHeight(max);
    };
    measureAll();
    window.addEventListener('resize', measureAll);
    return () => window.removeEventListener('resize', measureAll);
  }, []);

  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 100);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  useEffect(() => {
    fetch('/api/guided-questions')
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d?.questions?.length) setQuestions(d.questions); })
      .catch(() => {})
      .finally(() => setQuestionsLoading(false));
  }, []);

  useEffect(() => {
    if (prefersReducedMotion || ROTATING_PHRASES.length <= 1) return;
    const interval = setInterval(() => {
      setPhraseVisible(false);
      setTimeout(() => {
        setPhraseIndex(i => (i + 1) % ROTATING_PHRASES.length);
        setPhraseVisible(true);
      }, 220);
    }, 2600);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (quizShown && quizRef.current) {
      const t = setTimeout(() => {
        quizRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 80);
      return () => clearTimeout(t);
    }
  }, [quizShown]);

  const buildBrowseUrl = (finalAnswers) => {
    const params = new URLSearchParams();
    Object.values(finalAnswers).forEach(({ filter_param, filter_value }) => {
      if (filter_value && filter_value !== 'either') params.set(filter_param, filter_value);
    });
    const qs = params.toString();
    return `/browse${qs ? `?${qs}` : ''}`;
  };

  const logSearch = (finalAnswers, skippedIds = []) => {
    const params = {};
    Object.entries(finalAnswers).forEach(([, opt]) => { params[opt.filter_param] = opt.filter_value; });
    const skipped = questions.filter(q => skippedIds.includes(q.id)).map(q => q.question_text);
    fetch('/api/events', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        type: 'search_performed',
        properties: {
          ...params,
          search_text: quizSearchText || undefined,
          skipped_questions: skipped.join('; ') || undefined,
        },
      }),
    }).catch(() => {});
  };

  const handleSearch = (text) => {
    const trimmed = text.trim();
    if (!trimmed) return;

    if (questionsLoading || questions.length === 0) {
      const matched = matchCategory(trimmed);
      navigate(matched ? `/browse?category=${matched}` : '/browse');
      return;
    }

    const matched = matchCategory(trimmed);
    const catQIdx = questions.findIndex(q => q.options?.some(o => o.filter_param === 'category'));

    let initialAnswers = {};
    let initialStep = 0;

    if (matched && catQIdx >= 0) {
      const catQ = questions[catQIdx];
      const matchedOpt = catQ.options.find(o => o.filter_value === matched);
      if (matchedOpt) {
        initialAnswers = { [catQ.id]: matchedOpt };
        initialStep = catQIdx + 1;
        if (initialStep >= questions.length) {
          logSearch(initialAnswers, []);
          navigate(buildBrowseUrl(initialAnswers));
          return;
        }
      }
    }

    setQuizSearchText(trimmed);
    setQuizAnswers(initialAnswers);
    setQuizStep(initialStep);
    setQuizMinStep(initialStep);
    setQuizCardVisible(true);
    setQuizAnimating(false);
    setQuizShown(true);
  };

  const handleHintClick = (chip) => {
    setSearchInput(chip.label);
    handleSearch(chip.search);
  };

  const handleQuizOption = (option) => {
    if (quizAnimating) return;
    const newAnswers = { ...quizAnswers, [questions[quizStep].id]: option };
    setQuizAnswers(newAnswers);
    if (quizStep < questions.length - 1) {
      setQuizAnimating(true);
      setQuizCardVisible(false);
      setTimeout(() => { setQuizStep(s => s + 1); setQuizCardVisible(true); setQuizAnimating(false); }, 200);
    } else {
      logSearch(newAnswers, []);
      navigate(buildBrowseUrl(newAnswers));
    }
  };

  const handleQuizSkip = () => {
    if (quizAnimating) return;
    if (quizStep < questions.length - 1) {
      setQuizAnimating(true);
      setQuizCardVisible(false);
      setTimeout(() => { setQuizStep(s => s + 1); setQuizCardVisible(true); setQuizAnimating(false); }, 200);
    } else {
      const skippedIds = questions.filter(q => !quizAnswers[q.id]).map(q => q.id);
      logSearch(quizAnswers, skippedIds);
      navigate(buildBrowseUrl(quizAnswers));
    }
  };

  const handleQuizBack = () => {
    if (quizAnimating || quizStep <= quizMinStep) return;
    setQuizAnimating(true);
    setQuizCardVisible(false);
    setTimeout(() => { setQuizStep(s => s - 1); setQuizCardVisible(true); setQuizAnimating(false); }, 200);
  };

  const currentQ = questions[quizStep];

  return (
    <>
      <section style={{
        background: 'linear-gradient(160deg, #005a3e 0%, #004D35 50%, #003d2a 100%)',
        position: 'relative',
        overflow: 'hidden',
      }}>
        <ParticleCanvas />

        <div style={{
          position: 'absolute', bottom: 0, left: 0, right: 0, height: 120,
          background: 'linear-gradient(to bottom, transparent, #003d2a)',
          zIndex: 1, pointerEvents: 'none',
        }} />

        <div style={{
          maxWidth: 720,
          margin: '0 auto',
          padding: isMobile ? '100px 24px 64px' : '120px 40px 80px',
          textAlign: 'center',
          position: 'relative',
          zIndex: 2,
        }}>
          <p style={{
            fontSize: 11, fontFamily: 'sans-serif', letterSpacing: '0.2em',
            color: '#a8f0c6', fontWeight: 700, textTransform: 'uppercase', marginBottom: 20,
            opacity: visible ? 1 : 0,
            transform: visible ? 'translateY(0)' : 'translateY(10px)',
            transition: 'opacity 0.5s ease, transform 0.5s ease',
          }}>
            ◆ VERIFIED ADVISORS FOR YOUR BUSINESS
          </p>

          {/*
            The h1 is split into two visual lines:
              Line 1 — static text, never changes height
              Line 2 — fixed-height slot; height locked to the tallest phrase
                        measured at the current viewport width so the subheadline,
                        search bar, and chips never move when the phrase rotates.
          */}
          <h1 style={{
            fontSize: isMobile ? 'clamp(28px, 8vw, 40px)' : 'clamp(34px, 4.5vw, 56px)',
            fontWeight: 400,
            fontFamily: 'Georgia, serif', color: '#fff', lineHeight: 1.1, marginBottom: 20,
            opacity: visible ? 1 : 0,
            transform: visible ? 'translateY(0)' : 'translateY(16px)',
            transition: 'opacity 0.6s ease 0.1s, transform 0.6s ease 0.1s',
          }}>
            {/* Static first line */}
            <span style={{ display: 'block' }}>
              You didn&rsquo;t start your business to
            </span>

            {/*
              Fixed-height phrase slot.
              minHeight = max rendered height of all phrases at this viewport width,
              measured by the hidden <em ref={measureElRef}> below.
              Until the first measurement resolves (slotHeight === 0) the slot
              has no explicit height — the content provides natural height on
              the very first render (one frame, invisible due to entry animation).
            */}
            <span style={{
              display: 'block',
              position: 'relative',
              minHeight: slotHeight > 0 ? slotHeight : undefined,
            }}>
              {/* Visible animated phrase */}
              <em style={{
                color: '#a8f0c6',
                display: 'block',
                opacity: prefersReducedMotion ? 1 : (phraseVisible ? 1 : 0),
                transform: prefersReducedMotion ? 'none' : (phraseVisible ? 'translateY(0)' : 'translateY(6px)'),
                transition: prefersReducedMotion ? 'none' : 'opacity 0.22s ease, transform 0.22s ease',
              }}>
                {ROTATING_PHRASES[phraseIndex]}
              </em>

              {/*
                Hidden measurement element.
                - position: absolute so it is removed from flow and cannot affect
                  the slot's own height once slotHeight is set.
                - left: 0 / right: 0 gives it the same rendered width as the slot,
                  so wrapping behaviour matches the visible phrase exactly.
                - visibility: hidden keeps it out of the accessibility tree and
                  off-screen for sighted users.
                - Inherits font-size, font-family, line-height from the h1 above.
              */}
              <em
                ref={measureElRef}
                aria-hidden="true"
                style={{
                  position: 'absolute',
                  top: 0, left: 0, right: 0,
                  visibility: 'hidden',
                  pointerEvents: 'none',
                  display: 'block',
                }}
              />
            </span>
          </h1>

          <p style={{
            fontSize: isMobile ? 15 : 17, fontFamily: 'sans-serif',
            color: 'rgba(255,255,255,0.65)',
            maxWidth: 520, margin: '0 auto 40px',
            opacity: visible ? 1 : 0,
            transform: visible ? 'translateY(0)' : 'translateY(12px)',
            transition: 'opacity 0.6s ease 0.2s, transform 0.6s ease 0.2s',
          }}>
            Tell us what you&rsquo;re dealing with. We&rsquo;ll match you with a verified advisor who handles it every day.
          </p>

          <div style={{
            opacity: visible ? 1 : 0,
            transform: visible ? 'translateY(0)' : 'translateY(12px)',
            transition: 'opacity 0.6s ease 0.3s, transform 0.6s ease 0.3s',
          }}>
            <div style={{ position: 'relative', maxWidth: 560, margin: '0 auto 14px' }}>
              <input
                type="text"
                value={searchInput}
                onChange={e => setSearchInput(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleSearch(searchInput)}
                placeholder={'What do you need help with? Try \u201ctaxes\u201d or \u201cpayroll\u201d\u2026'}
                disabled={questionsLoading}
                style={{
                  width: '100%',
                  padding: isMobile ? '16px 20px' : '18px 192px 18px 24px',
                  borderRadius: 999,
                  border: 'none',
                  fontSize: isMobile ? 14 : 15,
                  fontFamily: 'sans-serif',
                  color: '#0a1a0f',
                  background: '#fff',
                  boxShadow: '0 8px 40px rgba(0,0,0,0.22)',
                  outline: 'none',
                  boxSizing: 'border-box',
                  opacity: questionsLoading ? 0.75 : 1,
                }}
              />
              {!isMobile && (
                <button
                  onClick={() => handleSearch(searchInput)}
                  disabled={questionsLoading}
                  style={{
                    position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)',
                    background: questionsLoading ? '#666' : '#004D35',
                    color: '#fff', border: 'none', borderRadius: 999,
                    padding: '11px 22px', fontSize: 14, fontWeight: 700, fontFamily: 'sans-serif',
                    cursor: questionsLoading ? 'default' : 'pointer', whiteSpace: 'nowrap',
                    transition: 'background 0.15s ease',
                  }}
                  onMouseEnter={e => { if (!questionsLoading) e.currentTarget.style.background = '#003d2a'; }}
                  onMouseLeave={e => { if (!questionsLoading) e.currentTarget.style.background = '#004D35'; }}
                >
                  Find my advisor
                </button>
              )}
            </div>

            {isMobile && (
              <button
                onClick={() => handleSearch(searchInput)}
                disabled={questionsLoading}
                style={{
                  width: '100%', maxWidth: 560, display: 'block', margin: '0 auto 14px',
                  background: '#004D35', color: '#fff', border: 'none', borderRadius: 999,
                  padding: '14px 24px', fontSize: 15, fontWeight: 700, fontFamily: 'sans-serif',
                  cursor: questionsLoading ? 'default' : 'pointer',
                }}
              >
                Find my advisor →
              </button>
            )}

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, justifyContent: 'center', marginBottom: 36 }}>
              {HINT_CHIPS.map(chip => (
                <button
                  key={chip.label}
                  onClick={() => handleHintClick(chip)}
                  disabled={questionsLoading}
                  style={{
                    background: 'rgba(255,255,255,0.10)',
                    border: '1.5px solid rgba(255,255,255,0.28)',
                    borderRadius: 999, padding: '7px 16px',
                    color: 'rgba(255,255,255,0.88)',
                    fontSize: 13, fontFamily: 'sans-serif',
                    cursor: questionsLoading ? 'default' : 'pointer',
                    transition: 'all 0.15s ease', whiteSpace: 'nowrap',
                  }}
                  onMouseEnter={e => { if (!questionsLoading) e.currentTarget.style.background = 'rgba(255,255,255,0.20)'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.10)'; }}
                >
                  {chip.label}
                </button>
              ))}
            </div>
          </div>

          <div style={{
            display: 'flex', justifyContent: 'center', gap: 32, flexWrap: 'wrap',
            opacity: visible ? 1 : 0,
            transition: 'opacity 0.6s ease 0.5s',
          }}>
            {['✓ All advisors verified', '✓ No commissions', '✓ Free to use'].map(t => (
              <span key={t} style={{ fontSize: 13, fontFamily: 'sans-serif', color: 'rgba(255,255,255,0.6)' }}>
                {t}
              </span>
            ))}
          </div>
        </div>
      </section>

      {quizShown && currentQ && (
        <div ref={quizRef} style={{ background: '#f5f3ef', padding: isMobile ? '32px 20px 48px' : '52px 40px 64px' }}>
          <div style={{ maxWidth: 620, margin: '0 auto' }}>
            <div style={{
              background: '#fff', borderRadius: 20,
              padding: isMobile ? '28px 24px' : '40px 48px',
              boxShadow: '0 8px 40px rgba(0,0,0,0.10)',
              opacity: quizCardVisible ? 1 : 0,
              transform: quizCardVisible ? 'translateY(0)' : 'translateY(8px)',
              transition: 'opacity 0.2s ease, transform 0.2s ease',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20, flexWrap: 'wrap' }}>
                <div style={{ display: 'flex', gap: 5 }}>
                  {Array.from({ length: questions.length }, (_, i) => (
                    <div key={i} style={{
                      width: i === quizStep ? 22 : 8, height: 8, borderRadius: 999,
                      background: i <= quizStep ? '#004D35' : '#e0ddd8',
                      transition: 'all 0.3s ease',
                    }} />
                  ))}
                </div>
                <span style={{ fontSize: 12, color: '#999', fontFamily: 'sans-serif' }}>
                  Step {quizStep + 1} of {questions.length}
                </span>
                {quizSearchText && (
                  <span style={{
                    fontSize: 11, fontFamily: 'sans-serif', fontWeight: 600,
                    background: 'rgba(0,77,53,0.08)', color: '#004D35',
                    padding: '3px 10px', borderRadius: 999,
                  }}>
                    🔍 {quizSearchText}
                  </span>
                )}
                {Object.values(quizAnswers).map((a, idx) => (
                  <span key={idx} style={{
                    fontSize: 11, fontFamily: 'sans-serif', fontWeight: 700,
                    background: 'rgba(0,77,53,0.1)', color: '#004D35',
                    padding: '3px 10px', borderRadius: 999,
                  }}>
                    {a.label}
                  </span>
                ))}
              </div>

              <h2 style={{
                fontSize: isMobile ? 18 : 22, fontWeight: 600, color: '#0a1a0f',
                fontFamily: 'Georgia, serif', marginBottom: 20,
              }}>
                {currentQ.question_text}
              </h2>

              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginBottom: 24 }}>
                {currentQ.options.map(opt => (
                  <button
                    key={opt.id}
                    onClick={() => handleQuizOption(opt)}
                    disabled={quizAnimating}
                    style={{
                      padding: '11px 22px', borderRadius: 999,
                      border: '1.5px solid #e0ddd8', background: '#fff',
                      color: '#0a1a0f', fontSize: 14, fontFamily: 'sans-serif',
                      fontWeight: 500, cursor: quizAnimating ? 'default' : 'pointer',
                      transition: 'all 0.15s ease',
                    }}
                    onMouseEnter={e => { if (!quizAnimating) { e.currentTarget.style.background = '#004D35'; e.currentTarget.style.color = '#fff'; e.currentTarget.style.borderColor = '#004D35'; } }}
                    onMouseLeave={e => { e.currentTarget.style.background = '#fff'; e.currentTarget.style.color = '#0a1a0f'; e.currentTarget.style.borderColor = '#e0ddd8'; }}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>

              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10 }}>
                <div style={{ display: 'flex', gap: 16 }}>
                  {quizStep > quizMinStep && (
                    <button onClick={handleQuizBack} style={{ background: 'none', border: 'none', fontSize: 13, fontFamily: 'sans-serif', color: '#aaa', cursor: 'pointer', padding: 0, textDecoration: 'underline', textUnderlineOffset: 3 }}>
                      ← Back
                    </button>
                  )}
                  <button onClick={handleQuizSkip} style={{ background: 'none', border: 'none', fontSize: 13, fontFamily: 'sans-serif', color: '#aaa', cursor: 'pointer', padding: 0, textDecoration: 'underline', textUnderlineOffset: 3 }}>
                    Skip this question →
                  </button>
                </div>
                <button onClick={() => navigate('/browse')} style={{ background: 'none', border: 'none', fontSize: 13, fontFamily: 'sans-serif', color: '#aaa', cursor: 'pointer', padding: 0, textDecoration: 'underline', textUnderlineOffset: 3 }}>
                  Just browse all advisors
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default HeroSection;
